/**
 * Lectura del informe de asistencia de Google Meet (el Sheet que Meet le manda a quien
 * organiza, descargado como CSV: Archivo → Descargar → .csv). Se busca la fila de
 * encabezados por nombre de columna (mail + duración), en inglés o castellano, así no
 * importa si el informe trae filas de título antes de la tabla.
 */

export interface MeetAttendanceRow {
  email: string;
  name: string;
  minutes: number;
  /** Meet tapa el mail de quien entró con una cuenta de afuera ("floo***@***.com"): no se
   *  puede cruzar con la plataforma, así que se muestra pero NO se carga. */
  masked?: boolean;
}

/** ¿Es un mail tapado por Meet? (trae asteriscos en vez de letras). */
export const isMaskedEmail = (email: string) => email.includes("*");

/** CSV mínimo con comillas ("a, b" y "" escapadas). Detecta `,` o `;` por la 1ª línea. */
function parseCsv(text: string): string[][] {
  const clean = text.replace(/^\uFEFF/, "");
  const firstLine = clean.split(/\r?\n/, 1)[0] ?? "";
  const sep = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < clean.length; i++) {
    const c = clean[i];
    if (inQuotes) {
      if (c === '"' && clean[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else cell += c;
    } else if (c === '"') inQuotes = true;
    else if (c === sep) { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && clean[i + 1] === "\n") i++;
      row.push(cell); rows.push(row); row = []; cell = "";
    } else cell += c;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

/** "1 hr 45 min", "1 h 45 min", "45 min", "1:45:10", "1:45" → minutos (redondeo hacia abajo). */
export function parseDurationToMinutes(raw: string): number | null {
  const v = raw.trim().toLowerCase();
  if (!v) return null;
  const colon = v.match(/^(\d+):(\d{1,2})(?::(\d{1,2}))?$/);
  if (colon) {
    // h:mm o h:mm:ss (los segundos se descartan).
    const [, a, b] = colon;
    return Number(a) * 60 + Number(b);
  }
  const h = v.match(/(\d+)\s*(h|hr|hrs|hora|horas)\b/);
  const m = v.match(/(\d+)\s*(m|min|mins|minuto|minutos)\b/);
  if (!h && !m) return /^\d+$/.test(v) ? Number(v) : null;
  return (h ? Number(h[1]) * 60 : 0) + (m ? Number(m[1]) : 0);
}

const norm = (s: string) => s.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const isEmailHeader = (c: string) => c.includes("mail") || c.includes("correo");
const isDurationHeader = (c: string) => c.includes("durac") || c.includes("duration");

/** Devuelve una fila por mail (minutos sumados si entró y salió varias veces). */
export function parseMeetAttendanceCsv(text: string): MeetAttendanceRow[] {
  const rows = parseCsv(text);
  const headerIdx = rows.findIndex((r) => {
    const cells = r.map(norm);
    return cells.some(isEmailHeader) && cells.some(isDurationHeader);
  });
  if (headerIdx === -1) {
    throw new Error("No encontramos las columnas de mail y duración en el archivo.");
  }
  const header = rows[headerIdx].map(norm);
  const emailCol = header.findIndex(isEmailHeader);
  const durCol = header.findIndex(isDurationHeader);
  const firstCol = header.findIndex((c) => c === "first name" || c === "nombre");
  const lastCol = header.findIndex((c) => c === "last name" || c === "apellido");

  const byEmail = new Map<string, MeetAttendanceRow>();
  for (const r of rows.slice(headerIdx + 1)) {
    const email = (r[emailCol] ?? "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) continue;
    const minutes = parseDurationToMinutes(r[durCol] ?? "") ?? 0;
    const name = [r[firstCol] ?? "", r[lastCol] ?? ""].join(" ").trim();
    const masked = isMaskedEmail(email);
    // Los tapados se agrupan por nombre: dos personas distintas pueden quedar con el mismo "***".
    const key = masked ? `masked:${name.toLowerCase()}` : email;
    const prev = byEmail.get(key);
    byEmail.set(key, {
      email,
      name: prev?.name || name,
      minutes: (prev?.minutes ?? 0) + minutes,
      ...(masked ? { masked: true } : {}),
    });
  }
  return Array.from(byEmail.values());
}
